import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import SEO from "../components/SEO";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const ensureRentalText = (value) => {
  if (!value) return "";

  const original = String(value).trim();
  const lower = original.toLowerCase();

  if (lower.includes("studio rental")) {
    return original;
  }

  return `${original} studio rental`;
};

const getTodayUK = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const formatDateDDMMYYYY = (value) => {
  if (!value) return "—";

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const FORM_DEFAULTS = {
  name: "",
  email: "",
  phone: "",
  purpose: "",
  notes: "",
};

export default function StudioRentals() {
  const { apiFetch } = useApi();
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const [rentalPlans, setRentalPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [planId, setPlanId] = useState(null);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loadingBlockedTimes, setLoadingBlockedTimes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "studio-rental-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["name", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  const minUkDate = useMemo(() => getTodayUK(), []);

  const timeSlots = useMemo(
    () => [
      "09:00",
      "10:00",
      "11:00",
      "12:00",
      "13:00",
      "14:00",
      "15:00",
      "16:00",
      "17:00",
    ],
    [],
  );

  const loadRentalPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      setPlansError("");

      const res = await apiFetch("get_studio_rentals");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load studio rentals.");
      }

      const mappedPlans = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          title: item.title,
          duration: ensureRentalText(item.duration),
          description: item.description,
          price: Number(item.price),
          durationHours: Number(item.duration_hours || 0),
        }),
      );

      setRentalPlans(mappedPlans);
      setPlanId((prev) =>
        prev && mappedPlans.some((plan) => plan.id === Number(prev))
          ? Number(prev)
          : mappedPlans.length
            ? mappedPlans[0].id
            : null,
      );
    } catch (err) {
      setPlansError(err.message || "Unable to load studio rentals.");
      setRentalPlans([]);
      setPlanId(null);
    } finally {
      setLoadingPlans(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadRentalPlans();
  }, [loadRentalPlans]);

  const selectedPlan = useMemo(() => {
    return (
      rentalPlans.find((plan) => Number(plan.id) === Number(planId)) || null
    );
  }, [rentalPlans, planId]);

  const isFullDay = useMemo(() => {
    return Number(selectedPlan?.durationHours || 0) === 9;
  }, [selectedPlan]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const loadUnavailableTimes = useCallback(async () => {
    if (!date || !selectedPlan?.id) {
      setBlockedTimes([]);
      return;
    }

    try {
      setLoadingBlockedTimes(true);

      const res = await apiFetch("get_studio_rental_unavailable_times", {
        bookingDate: date,
        rentalPlanId: selectedPlan.id,
      });

      if (!res?.success) {
        throw new Error(res?.message || "Unable to load unavailable times.");
      }

      const blocked = Array.isArray(res.blockedTimes)
        ? res.blockedTimes
        : Array.isArray(res.data?.blockedTimes)
          ? res.data.blockedTimes
          : [];

      setBlockedTimes(blocked);
    } catch (err) {
      setBlockedTimes([]);
      showError(err.message || "Unable to check studio availability.");
    } finally {
      setLoadingBlockedTimes(false);
    }
  }, [apiFetch, date, selectedPlan, showError]);

  useEffect(() => {
    loadUnavailableTimes();
  }, [loadUnavailableTimes]);

  useEffect(() => {
    if (isFullDay) {
      setTime("09:00");
      return;
    }

    if (time && blockedTimes.includes(time)) {
      setTime("");
    }
  }, [isFullDay, blockedTimes, time]);

  const onSelectPlan = (id) => {
    setPlanId(id);
    setTime("");

    if (isErrorOpen) {
      closeError();
    }
  };

  const validateForm = () => {
    if (!selectedPlan) return "Please select a rental duration.";
    if (!date) return "Please choose a date.";
    if (!time) return "Please choose a start time.";
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim()) return "Please enter your email address.";
    if (!form.purpose.trim()) return "Please enter the purpose of the rental.";

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return "Please enter a valid email address.";

    if (!form.phone.trim()) return "Please enter your phone number.";
    if (blockedTimes.includes(time))
      return "The selected time is no longer available.";

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    if (submitting || !selectedPlan) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        rentalPlanId: selectedPlan.id,
        bookingDate: date,
        startTime: time,
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        purpose: form.purpose.trim(),
        notes: form.notes.trim(),
      };

      const res = await apiFetch(
        "create_studio_rental_checkout_session",
        payload,
      );

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkoutUrl) {
        throw new Error("Stripe checkout link was not returned.");
      }

      window.location.href = res.checkoutUrl;
    } catch (err) {
      showError(err.message || "Unable to start payment.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Studio Rentals for Photoshoots | Kinews Studio Limited"
        description="Book a fully equipped photography studio for your photoshoots, content creation and creative projects at Kinews Studio Limited."
        url={window.location.href}
      />

      <main className="min-h-screen bg-[#070707] text-white">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"] {
            color-scheme: dark;
          }
        `}</style>

        <section className="relative overflow-hidden bg-[#050505] text-white">
          {/* Lighting Effects */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-[10%] -top-[10%] h-[40rem] w-[40rem] rounded-full bg-pink-500/15 blur-[120px]" />
            <div className="absolute -right-[10%] top-[20%] h-[35rem] w-[35rem] rounded-full bg-pink-600/10 blur-[100px]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[50rem] w-[50rem] rounded-full bg-pink-500/5 blur-[150px]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pb-24 md:pt-40">
            <h1 className="mt-3 text-4xl font-bold tracking-tighter md:text-5xl lg:text-6xl">
              Rentals
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 md:text-xl">
              Fully-equipped studio space for your next creative project.
            </p>
          </div>
        </section>

        <section className="bg-[#0d0d0f]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                Step 1
              </p>
              <h2 className="mt-2 text-3xl font-semibold">
                Select rental duration
              </h2>
            </div>

            {loadingPlans ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                Loading studio rentals...
              </div>
            ) : plansError ? (
              <div className="rounded-3xl border border-red-500/20 bg-red-500/10 p-8 text-red-200">
                {plansError}
              </div>
            ) : rentalPlans.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                No studio rentals are available right now.
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {rentalPlans.map((plan) => {
                  const active = Number(plan.id) === Number(planId);

                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => onSelectPlan(plan.id)}
                      className={[
                        "relative overflow-hidden rounded-3xl border p-6 text-left transition-all duration-300",
                        active
                          ? "border-pink-500 bg-gradient-to-b from-pink-500/20 to-white/5 shadow-[0_0_0_1px_rgba(236,72,153,0.2),0_20px_50px_rgba(0,0,0,0.35)]"
                          : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05]",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm uppercase tracking-[0.2em] text-white/40">
                            Rental
                          </p>
                          <h3 className="mt-2 text-2xl font-semibold">
                            {plan.title}
                          </h3>
                        </div>

                        <div
                          className={[
                            "rounded-full px-3 py-1 text-sm font-semibold",
                            active
                              ? "bg-pink-500 text-white"
                              : "bg-white/10 text-white/70",
                          ].join(" ")}
                        >
                          {money(plan.price)}
                        </div>
                      </div>

                      <div className="mt-8 space-y-4">
                        <div className="flex items-center gap-2 text-sm text-white/75">
                          <span className="h-2 w-2 rounded-full bg-pink-400" />
                          <span>{plan.duration}</span>
                        </div>

                        {plan.description && (
                          <p className="text-sm leading-6 text-white/60">
                            {plan.description}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#09090b]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <div className="mb-8">
              <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                Step 2
              </p>
              <h2 className="mt-2 text-3xl font-semibold">
                Choose date and time
              </h2>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                <label className="text-sm font-medium text-white/70">
                  Preferred date
                </label>

                <div className="relative mt-3 min-w-0">
                  <input
                    type="date"
                    min={minUkDate}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setTime("");

                      if (isErrorOpen) {
                        closeError();
                      }
                    }}
                    className="w-full min-w-0 appearance-none rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition focus:border-pink-500"
                  />

                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-white/80"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                <label className="text-sm font-medium text-white/70">
                  Start time
                </label>

                {!date ? (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white/50">
                    Select a date first.
                  </div>
                ) : loadingBlockedTimes ? (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white/50">
                    Checking availability...
                  </div>
                ) : isFullDay ? (
                  <div className="mt-3 rounded-2xl border border-pink-500/20 bg-pink-500/10 px-4 py-4 text-sm text-pink-100">
                    Full day bookings run from 09:00 to 18:00.
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {timeSlots.map((slot) => {
                      const active = time === slot;
                      const blocked = blockedTimes.includes(slot);

                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => {
                            if (blocked) return;
                            setTime(slot);

                            if (isErrorOpen) {
                              closeError();
                            }
                          }}
                          disabled={blocked}
                          className={[
                            "rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                            blocked
                              ? "cursor-not-allowed border-white/10 bg-white/5 text-white/30"
                              : active
                                ? "border-pink-500 bg-pink-500 text-white shadow-lg shadow-pink-500/20"
                                : "border-white/10 bg-black/20 text-white/80 hover:border-white/20 hover:bg-white/5",
                          ].join(" ")}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0d0d0f]">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-12">
            <form
              id="studio-rental-form"
              noValidate
              onSubmit={onSubmit}
              className="space-y-5 rounded-3xl border border-white/10 bg-white/[0.03] p-7 lg:col-span-7"
            >
              <div>
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Step 3
                </p>
                <h2 className="mt-2 text-3xl font-semibold">Your details</h2>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-white/70">Full name</label>
                <input
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition focus:border-pink-500"
                  value={form.name}
                  onChange={onChange("name")}
                  required
                  autoComplete="name"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-white/70">Email address</label>
                <input
                  type="email"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition focus:border-pink-500"
                  value={form.email}
                  onChange={onChange("email")}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-white/70">Phone number</label>
                <input
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition focus:border-pink-500"
                  value={form.phone}
                  onChange={onChange("phone")}
                  required
                  autoComplete="tel"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-white/70">Purpose of rental</label>
                <input
                  placeholder="e.g. photoshoot, video shoot"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-pink-500"
                  value={form.purpose}
                  onChange={onChange("purpose")}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-white/70">Additional notes</label>
                <textarea
                  rows={5}
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none transition focus:border-pink-500"
                  value={form.notes}
                  onChange={onChange("notes")}
                />
              </div>
            </form>

            <aside className="lg:col-span-5">
              <div className="sticky top-6 rounded-3xl border border-pink-500/20 bg-gradient-to-b from-[#181116] to-[#0d0b0d] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Summary
                </p>
                <h3 className="mt-2 text-2xl font-semibold">Rental overview</h3>

                {!selectedPlan ? (
                  <div className="mt-8 text-sm text-white/70">
                    No rental plan selected.
                  </div>
                ) : (
                  <div className="mt-8 space-y-4 text-sm text-white/75">
                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Rental plan</span>
                      <span className="text-right font-medium text-white">
                        {selectedPlan.duration}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Date</span>
                      <span className="text-right font-medium text-white">
                        {formatDateDDMMYYYY(date)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Start time</span>
                      <span className="text-right font-medium text-white">
                        {time || "—"}
                      </span>
                    </div>

                    <div className="mt-6 rounded-2xl bg-white/5 px-4 py-4">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-white">
                          Total
                        </span>
                        <span className="text-2xl font-semibold text-pink-400">
                          {money(selectedPlan.price)}
                        </span>
                      </div>
                    </div>

                    <button
                      type="submit"
                      form="studio-rental-form"
                      disabled={
                        submitting || loadingPlans || loadingBlockedTimes
                      }
                      className={[
                        "w-full rounded-2xl px-6 py-4 text-base font-semibold transition",
                        submitting || loadingPlans || loadingBlockedTimes
                          ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                          : "bg-pink-500 text-white hover:bg-pink-600",
                      ].join(" ")}
                    >
                      {submitting ? "Redirecting..." : "Continue to Payment"}
                    </button>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}
